import { ChartTable } from './ChartTable';
import { niceMax, SERIES_BG, SERIES_FILL, type SeriesColour, ticks } from './scale';
import { useChartWidth } from './useChartWidth';
import { cn } from '@/util/utils';

export interface BarSeries {
    label: string;
    /** The figures, as the hidden table states them. */
    values: number[];
    colour: SeriesColour;
    /**
     * What the bars draw, when that differs from the figure: a stack whose segments must add up
     * to a whole draws a part of one segment, while the table still states it in full. Defaults
     * to `values`.
     */
    drawn?: number[];
}

/** The height a series draws at for a bar; nothing below the axis. */
const drawnAt = (s: BarSeries, i: number) => Math.max(0, (s.drawn ?? s.values)[i] ?? 0);

export interface BarChartProps {
    /** One per bar, oldest first. */
    labels: string[];
    /** Drawn bottom to top when stacked, side by side otherwise. */
    series: BarSeries[];
    stacked?: boolean;
    /** A note above each bar, such as its margin; null leaves that bar bare. */
    topLabels?: (string | null)[];
    format: (value: number) => string;
    /** What the chart shows, for the screen reader's summary and the hidden table's caption. */
    title: string;
    height?: number;
    className?: string;
}

const LEFT = 48;
const BOTTOM = 24;
const TOP = 18;

/**
 * The Reports bar chart (charts.js `stacked` and the bars of `combo`): value gridlines, one
 * group per label, an optional note over each bar, and a legend under. Its viewBox is the width it
 * is shown at (useChartWidth), so it fills whatever holds it and its labels keep their size.
 */
export function BarChart({
    labels,
    series,
    stacked = false,
    topLabels,
    format,
    title,
    height = 230,
    className,
}: BarChartProps) {
    // Drawn at the width it is shown at, so its labels stay 11px on a phone.
    const [figure, width] = useChartWidth<HTMLElement>();
    const totals = labels.map((_, i) =>
        stacked
            ? series.reduce((sum, s) => sum + drawnAt(s, i), 0)
            : Math.max(0, ...series.map((s) => drawnAt(s, i))),
    );
    const top = niceMax(Math.max(0, ...totals));
    const hasValues = totals.some((total) => total > 0);
    const plot = { width: width - LEFT, height: height - TOP - BOTTOM };
    const band = plot.width / Math.max(1, labels.length);
    const barWidth = Math.min(48, band * (stacked ? 0.56 : 0.72 / Math.max(1, series.length)));
    const y = (value: number) => TOP + plot.height - (value / top) * plot.height;

    return (
        <figure ref={figure} className={cn('m-0 flex min-w-0 flex-col gap-2', className)}>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="block h-auto w-full"
                role="img"
                aria-label={title}
            >
                {ticks(top, 4, hasValues).map((tick) => (
                    <g key={tick}>
                        <line
                            x1={LEFT}
                            x2={width}
                            y1={y(tick)}
                            y2={y(tick)}
                            className="stroke-border"
                            strokeWidth={1}
                        />
                        <text
                            x={LEFT - 8}
                            y={y(tick) + 4}
                            textAnchor="end"
                            className="fill-muted-foreground text-[11px]"
                        >
                            {format(tick)}
                        </text>
                    </g>
                ))}
                {labels.map((label, i) => {
                    const centre = LEFT + band * i + band / 2;
                    return (
                        <g key={label} data-bar={label}>
                            {series.map((s, si) => {
                                const value = drawnAt(s, i);
                                const x = stacked
                                    ? centre - barWidth / 2
                                    : centre - (barWidth * series.length) / 2 + barWidth * si;
                                // A stacked segment starts where the ones under it end.
                                const from = stacked
                                    ? series
                                          .slice(0, si)
                                          .reduce((sum, below) => sum + drawnAt(below, i), 0)
                                    : 0;
                                return (
                                    <rect
                                        key={s.label}
                                        data-series={s.label}
                                        x={x}
                                        y={y(from + value)}
                                        width={barWidth}
                                        height={Math.max(0, y(from) - y(from + value))}
                                        rx={2}
                                        className={SERIES_FILL[s.colour]}
                                    />
                                );
                            })}
                            {topLabels?.[i] ? (
                                <text
                                    x={centre}
                                    y={y(totals[i] ?? 0) - 6}
                                    textAnchor="middle"
                                    className="fill-foreground text-[11px] font-semibold"
                                >
                                    {topLabels[i]}
                                </text>
                            ) : null}
                            <text
                                x={centre}
                                y={height - 6}
                                textAnchor="middle"
                                className="fill-muted-foreground text-[11px]"
                            >
                                {label}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <figcaption className="flex flex-wrap justify-center gap-4">
                {series.map((s) => (
                    <span
                        key={s.label}
                        className="text-muted-foreground inline-flex items-center gap-1.5 text-xs"
                    >
                        <i
                            aria-hidden
                            className={cn('size-2.5 shrink-0 rounded-full', SERIES_BG[s.colour])}
                        />
                        {s.label}
                    </span>
                ))}
            </figcaption>
            <ChartTable
                caption={title}
                columns={['', ...series.map((s) => s.label), ...(topLabels ? [''] : [])]}
                rows={labels.map((label, i) => ({
                    label,
                    cells: [
                        ...series.map((s) => format(s.values[i] ?? 0)),
                        ...(topLabels ? [topLabels[i] ?? ''] : []),
                    ],
                }))}
            />
        </figure>
    );
}
